/**
 * Getting a roof measured.
 *
 * Two ways, and the rep picks one per job: draw it themselves in iRoofing, or
 * order a report from GAF QuickMeasure and wait for it to arrive. Neither
 * publishes an API or a CRM link — nothing we write can put an address into one,
 * press submit and start the job for a rep (ADR 0091, ADR 0109). What we can do
 * is stop the retyping, which is the part that actually costs time: the customer
 * and the service address go on the clipboard, the service opens, one paste
 * finds the house. The measurement comes back the way every measurement already
 * does — dropped on the Roof Measurements card, where the numbers are read off
 * it.
 *
 * The wording lives here rather than in the component so the MCP server can hand
 * an agent the same two lines, and so it is tested.
 */

import { jobAddress } from "./search.js";
import type { Job } from "./types.js";

/**
 * iRoofing's web app.
 *
 * Deliberately the bare app rather than a page inside it: iRoofing publishes no
 * link format, so anything cleverer is a guess that breaks silently the day they
 * move a screen. This lands the rep wherever iRoofing puts them, signed in.
 */
export const IROOFING_URL = "https://app.iroofing.org/";

/**
 * GAF QuickMeasure, where a report is ordered rather than drawn.
 *
 * Jonathon's link, 2026-09-06. The same caution applies — no documented link
 * that carries an address — so this is the page he named and nothing cleverer.
 */
export const QUICKMEASURE_URL = "https://quickmeasure.gaf.com/home-page";

/** Which service the rep chose. */
export type MeasureServiceKey = "iroofing" | "quickmeasure";

export interface MeasureService {
  key: MeasureServiceKey;
  /** The service's own name, as the rep says it. */
  name: string;
  /** The choice, as it reads on the button that picks it. */
  choice: string;
  /** One line under that button saying what happens next. */
  blurb: string;
  /** Where the button goes. */
  url: string;
  /** What the button that copies and opens says. */
  action: string;
  /** What comes back, and where it lands. */
  returns: string;
}

/**
 * The two ways to get a roof measured, in the order they are offered.
 *
 * iRoofing first because it is free to us and immediate; QuickMeasure is the
 * one to reach for when the roof is not worth drawing by hand or the imagery is
 * no good.
 */
export const MEASURE_SERVICES: readonly MeasureService[] = [
  {
    key: "iroofing",
    name: "iRoofing",
    choice: "Measure it in iRoofing",
    blurb: "Draw the roof yourself, now. The address goes on your clipboard.",
    url: IROOFING_URL,
    action: "Copy address & open iRoofing",
    returns:
      "iRoofing cannot be filled in from here — it has no connection to Roof Board — so the paste is the handover. When the report is finished, come back to Roof Measurements and drop the PDF on it: the measurements are read off it for you to confirm.",
  },
  {
    key: "quickmeasure",
    name: "QuickMeasure",
    choice: "Order a QuickMeasure report",
    blurb:
      "GAF measures it for you and emails the report back. The address goes on your clipboard.",
    url: QUICKMEASURE_URL,
    action: "Copy address & open QuickMeasure",
    returns:
      "QuickMeasure has no connection to Roof Board either, so the order is placed on their site with the address you just copied. When the report arrives by email, drop the PDF on Roof Measurements: the measurements are read off it for you to confirm.",
  },
];

/** One service by key. Unknown keys fall back to iRoofing rather than throw. */
export function measureService(key: MeasureServiceKey): MeasureService {
  return MEASURE_SERVICES.find((s) => s.key === key) ?? MEASURE_SERVICES[0]!;
}

export interface MeasureHandoff {
  /** Which service this handoff is for. */
  service: MeasureService;
  /** Where the button goes. */
  url: string;
  /** Paste this into the service's address search. */
  address: string;
  /** The customer, for the service's own name for the job. */
  customerName: string;
  /** Customer and address, two lines, for taking both at once. */
  text: string;
  /** Why the handoff cannot be made yet, or null when it can. */
  blocked: string | null;
}

/**
 * What to hand the measuring service for this job.
 *
 * The address is the job's own, falling back to the customer's while the job has
 * none — `jobAddress`, the same rule every other screen uses (ADR 0003). Without
 * an address there is nothing to paste and the handoff says so rather than
 * opening the service at a blank search.
 *
 * The service defaults to iRoofing, which is what this was before QuickMeasure
 * joined it.
 */
export function measureHandoff(
  job: Job,
  service: MeasureServiceKey = "iroofing",
): MeasureHandoff {
  const chosen = measureService(service);
  const address = jobAddress(job);
  const customerName = (job.contact?.full_name ?? "").trim();

  return {
    service: chosen,
    url: chosen.url,
    address,
    customerName,
    text: [customerName, address].filter(Boolean).join("\n"),
    blocked: address
      ? null
      : `This job has no address yet. Add one on the Overview card and there will be something to hand to ${chosen.name}.`,
  };
}
